import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Brain, Target, Briefcase, Sparkles, 
  ChevronRight, ChevronLeft, Loader2, CheckCircle, 
  Edit2, Trash2, Plus, RefreshCw, Save, ArrowRight,
  User, Building2, TrendingUp, Lightbulb, Award,
  Search, BookOpen, Beaker, Trophy, Play, Route
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { API_BASE } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const API = `${API_BASE}/api`;

// NAVI Avatar
const NAVI_AVATAR = "https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/f47qg3qu_image.png";

// Areas of Work
const AREAS_OF_WORK = [
  "Governance",
  "Risk",
  "Compliance",
  "AML / Financial Crime",
  "GRC Operations",
  "IT / Technology",
  "Cybersecurity",
  "Data",
  "Data Management",
  "Privacy",
  "AI Governance",
  "Digital Transformation",
  "Strategy",
  "Other"
];

// Experience Levels
const EXPERIENCE_LEVELS = [
  { value: "Foundation", label: "Foundation", description: "Entry-level, learning fundamentals" },
  { value: "Developing", label: "Developing", description: "Building skills, some experience" },
  { value: "Experienced", label: "Experienced", description: "Solid experience, works independently" },
  { value: "Advanced", label: "Advanced", description: "Expert level, mentors others" },
  { value: "Leadership", label: "Leadership", description: "Strategic leadership, shapes direction" }
];

// Target Levels for competencies
const TARGET_LEVELS = ["Foundation", "Applied", "Proficient", "Advanced", "Strategic"];

// Competency Categories
const COMPETENCY_CATEGORIES = [
  "Technical Competence",
  "Regulatory & Compliance Competence",
  "Digital & Technology Competence",
  "Critical Thinking & Problem Solving",
  "Professional Judgement",
  "Communication & Stakeholder Engagement",
  "Documentation & Evidence Management",
  "Ethics & Professional Conduct",
  "Behavioural & Workplace Effectiveness",
  "Leadership & Strategic Capability"
];

// Category colors
const CATEGORY_COLORS = {
  "Technical Competence": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Regulatory & Compliance Competence": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "Digital & Technology Competence": "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  "Critical Thinking & Problem Solving": "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "Professional Judgement": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  "Communication & Stakeholder Engagement": "bg-pink-500/20 text-pink-400 border-pink-500/30",
  "Documentation & Evidence Management": "bg-slate-500/20 text-slate-400 border-slate-500/30",
  "Ethics & Professional Conduct": "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
  "Behavioural & Workplace Effectiveness": "bg-orange-500/20 text-orange-400 border-orange-500/30",
  "Leadership & Strategic Capability": "bg-rose-500/20 text-rose-400 border-rose-500/30"
};

// Level colors
const LEVEL_COLORS = {
  "Foundation": "bg-green-500/20 text-green-400",
  "Applied": "bg-blue-500/20 text-blue-400",
  "Proficient": "bg-amber-500/20 text-amber-400",
  "Advanced": "bg-purple-500/20 text-purple-400",
  "Strategic": "bg-rose-500/20 text-rose-400"
};

export default function CompetencyFrameworkTab() {
  const { token } = useAuth();
  
  // View mode: 'saved' shows existing frameworks, 'create' shows wizard
  const [viewMode, setViewMode] = useState('saved');
  const [savedFrameworks, setSavedFrameworks] = useState([]);
  const [loadingFrameworks, setLoadingFrameworks] = useState(true);
  const [selectedFramework, setSelectedFramework] = useState(null);
  
  // Competency Learning Pathway items
  const [pathwayItems, setPathwayItems] = useState([]);
  const [loadingPathwayItems, setLoadingPathwayItems] = useState(false);
  
  // Wizard state
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  
  // User inputs
  const [inputs, setInputs] = useState({
    current_role: '',
    area_of_work: [], // Changed to array for multi-select
    experience_level: '',
    learning_goals: '',
    career_goal: ''
  });
  
  // Generated competencies
  const [competencies, setCompetencies] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [frameworkName, setFrameworkName] = useState('My Competency Framework');
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCompetency, setEditingCompetency] = useState(null);
  
  // Add dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newCompetency, setNewCompetency] = useState({
    name: '',
    category: '',
    definition: '',
    measurable_outcome: '',
    target_level: 'Applied',
    skills_required: [],
    evidence_of_competence: ''
  });
  
  const headers = { Authorization: `Bearer ${token}` };
  
  // Load saved frameworks and pathway items on mount
  useEffect(() => {
    fetchSavedFrameworks();
    fetchCompetencyPathwayItems();
  }, []);
  
  const fetchSavedFrameworks = async () => {
    setLoadingFrameworks(true);
    try {
      const response = await axios.get(`${API}/competencies/framework/my`, { headers });
      setSavedFrameworks(response.data.frameworks || []);
      if (response.data.frameworks?.length > 0) {
        setSelectedFramework(response.data.frameworks[0]);
      }
    } catch (error) {
      console.error('Failed to load frameworks:', error);
    } finally {
      setLoadingFrameworks(false);
    }
  };
  
  const fetchCompetencyPathwayItems = async () => {
    setLoadingPathwayItems(true);
    try {
      const response = await axios.get(`${API}/competencies/my-learning-pathway`, { headers });
      setPathwayItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to load pathway items:', error);
    } finally {
      setLoadingPathwayItems(false);
    }
  };
  
  // Validation
  const isStep1Valid = inputs.current_role.trim().length > 0;
  const isStep2Valid = inputs.area_of_work.length > 0 && inputs.experience_level;
  const isStep3Valid = inputs.learning_goals.trim().length > 0 && inputs.career_goal.trim().length > 0;
  
  // Generate competencies
  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/competencies/generate`, { inputs }, { headers });
      setCompetencies(response.data.competencies || []);
      setStep(4);
      toast.success('Competencies generated successfully!');
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error('Failed to generate competencies. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Regenerate
  const handleRegenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/competencies/generate`, { inputs }, { headers });
      setCompetencies(response.data.competencies || []);
      toast.success('Competencies regenerated!');
    } catch (error) {
      console.error('Regeneration failed:', error);
      toast.error('Failed to regenerate competencies.');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Save framework
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await axios.post(`${API}/competencies/framework/save`, {
        name: frameworkName,
        inputs,
        competencies
      }, { headers });
      
      toast.success('Competency framework saved!');
      fetchSavedFrameworks();
      setViewMode('saved');
      resetWizard();
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save framework.');
    } finally {
      setIsSaving(false);
    }
  };
  
  const resetWizard = () => {
    setStep(1);
    setInputs({
      current_role: '',
      area_of_work: [],
      experience_level: '',
      learning_goals: '',
      career_goal: ''
    });
    setCompetencies([]);
    setFrameworkName('My Competency Framework');
  };
  
  // Edit competency
  const handleEdit = (competency) => {
    setEditingCompetency({ ...competency });
    setEditDialogOpen(true);
  };
  
  const handleSaveEdit = () => {
    setCompetencies(prev => 
      prev.map(c => c.competency_id === editingCompetency.competency_id ? editingCompetency : c)
    );
    setEditDialogOpen(false);
    setEditingCompetency(null);
    toast.success('Competency updated');
  };
  
  // Delete competency
  const handleDelete = (competencyId) => {
    setCompetencies(prev => prev.filter(c => c.competency_id !== competencyId));
    toast.success('Competency removed');
  };
  
  // Add competency
  const handleAdd = () => {
    const id = `comp_${Date.now().toString(36)}`;
    setCompetencies(prev => [...prev, { ...newCompetency, competency_id: id }]);
    setAddDialogOpen(false);
    setNewCompetency({
      name: '',
      category: '',
      definition: '',
      measurable_outcome: '',
      target_level: 'Applied',
      skills_required: [],
      evidence_of_competence: ''
    });
    toast.success('Competency added');
  };
  
  // Can proceed to next step
  const canProceed = () => {
    switch (step) {
      case 1: return isStep1Valid;
      case 2: return isStep2Valid;
      case 3: return isStep3Valid;
      default: return false;
    }
  };
  
  // Render saved frameworks view
  const renderSavedView = () => {
    if (loadingFrameworks) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        </div>
      );
    }
    
    if (savedFrameworks.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500/20 to-amber-500/20 mb-4">
            <Brain className="h-8 w-8 text-teal-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No Competency Framework Yet</h3>
          <p className="text-zinc-400 mb-6 max-w-md mx-auto">
            Create your personalized competency framework to map your career progression and discover relevant learning content.
          </p>
          <Button
            onClick={() => setViewMode('create')}
            className="bg-gradient-to-r from-teal-500 to-amber-500 hover:from-teal-600 hover:to-amber-600"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Create My Framework
          </Button>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={NAVI_AVATAR} alt="NAVI" className="w-10 h-10 rounded-full" />
            <div>
              <h2 className="text-xl font-bold text-white">Your Competency Framework</h2>
              <p className="text-sm text-zinc-400">Powered by NAVI</p>
            </div>
          </div>
          {savedFrameworks.length > 0 && (
            <Button
              onClick={() => { resetWizard(); setViewMode('create'); }}
              variant="outline"
              className="border-zinc-700"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Rebuild Framework
            </Button>
          )}
        </div>
        
        {selectedFramework && (
          <>
            {/* Summary Card */}
            <div className="bg-gradient-to-br from-teal-500/10 to-amber-500/10 rounded-xl p-5 border border-teal-500/20">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-zinc-500">Current Role</p>
                  <p className="text-white font-medium">{selectedFramework.inputs?.current_role}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Area(s)</p>
                  <p className="text-white font-medium">
                    {Array.isArray(selectedFramework.inputs?.area_of_work) 
                      ? selectedFramework.inputs.area_of_work.join(', ')
                      : selectedFramework.inputs?.area_of_work}
                  </p>
                </div>
                <div>
                  <p className="text-zinc-500">Experience</p>
                  <p className="text-white font-medium">{selectedFramework.inputs?.experience_level}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Career Goal</p>
                  <p className="text-amber-400 font-medium">{selectedFramework.inputs?.career_goal}</p>
                </div>
              </div>
            </div>
            
            {/* Competencies */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-zinc-400">
                {selectedFramework.competencies?.length || 0} Competencies
              </h3>
              {selectedFramework.competencies?.map((competency, index) => (
                <div
                  key={competency.competency_id || index}
                  className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-white">{competency.name}</h4>
                        <Badge className={LEVEL_COLORS[competency.target_level] || 'bg-zinc-500/20'}>
                          {competency.target_level}
                        </Badge>
                      </div>
                      <Badge variant="outline" className={CATEGORY_COLORS[competency.category] || 'border-zinc-600'}>
                        {competency.category}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-sm text-zinc-400 mt-2">{competency.definition}</p>
                  <div className="mt-3 pt-3 border-t border-zinc-700/50">
                    <p className="text-xs text-zinc-500">
                      <strong className="text-zinc-400">Measurable Outcome:</strong> {competency.measurable_outcome}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* My Competency Learning Pathway */}
            <div className="bg-zinc-900/50 rounded-xl p-5 border border-zinc-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Route className="h-5 w-5 text-teal-400" />
                  <h3 className="font-semibold text-white">My Competency Learning Pathway</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="border-zinc-700 text-xs" onClick={() => window.location.href = '/courses'}>
                    <BookOpen className="h-3 w-3 mr-1" />
                    Browse Courses
                  </Button>
                  <Button variant="outline" size="sm" className="border-zinc-700 text-xs" onClick={() => window.location.href = '/labs'}>
                    <Beaker className="h-3 w-3 mr-1" />
                    Labs
                  </Button>
                </div>
              </div>
              
              {loadingPathwayItems ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-teal-500" />
                </div>
              ) : pathwayItems.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-zinc-500 text-sm mb-3">
                    No courses added yet. Browse courses, learning labs, masterclasses, and campaigns to add content to your competency learning pathway.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {pathwayItems.map((item, index) => (
                    <div
                      key={item.item_id || index}
                      className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50 hover:border-zinc-600 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          item.item_type === 'course' ? 'bg-blue-500/20' :
                          item.item_type === 'learning_lab' ? 'bg-purple-500/20' :
                          item.item_type === 'masterclass' ? 'bg-amber-500/20' :
                          'bg-teal-500/20'
                        }`}>
                          {item.item_type === 'course' && <BookOpen className="h-4 w-4 text-blue-400" />}
                          {item.item_type === 'learning_lab' && <Beaker className="h-4 w-4 text-purple-400" />}
                          {item.item_type === 'masterclass' && <Play className="h-4 w-4 text-amber-400" />}
                          {item.item_type === 'campaign' && <Trophy className="h-4 w-4 text-teal-400" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{item.item_title}</p>
                          <p className="text-xs text-zinc-500 capitalize">{item.item_type?.replace('_', ' ')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.completed ? (
                          <Badge className="bg-green-500/20 text-green-400 text-xs">Completed</Badge>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs border-zinc-600 h-7"
                            onClick={() => {
                              const url = item.item_type === 'course' ? `/courses/${item.item_id}` :
                                         item.item_type === 'learning_lab' ? `/labs/${item.item_id}` :
                                         item.item_type === 'masterclass' ? `/masterclasses/${item.item_id}` :
                                         `/campaigns/${item.item_id}`;
                              window.location.href = url;
                            }}
                          >
                            Continue
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };
  
  // Render wizard step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500/20 to-amber-500/20 mb-3">
                <User className="h-7 w-7 text-teal-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-1">What is your current role?</h2>
              <p className="text-zinc-400 text-sm">Tell us about your current job title or position</p>
            </div>
            
            <div className="max-w-lg mx-auto">
              <Label htmlFor="current_role" className="text-zinc-300 mb-2 block">Current Job Title / Role</Label>
              <Input
                id="current_role"
                placeholder="e.g., Compliance Officer, Risk Analyst, IT Governance Manager..."
                value={inputs.current_role}
                onChange={(e) => setInputs({ ...inputs, current_role: e.target.value })}
                className="bg-zinc-800/50 border-zinc-700 text-white h-11"
                data-testid="input-current-role"
              />
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 mb-3">
                <Building2 className="h-7 w-7 text-purple-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-1">Your Area & Experience</h2>
              <p className="text-zinc-400 text-sm">Select your areas of work (multi-select) and experience level</p>
            </div>
            
            <div className="max-w-lg mx-auto space-y-5">
              <div>
                <Label className="text-zinc-300 mb-2 block">
                  Areas of Work <span className="text-zinc-500 text-xs ml-1">(Select all that apply)</span>
                </Label>
                <div className="grid grid-cols-2 gap-2 max-h-[240px] overflow-y-auto pr-2">
                  {AREAS_OF_WORK.map((area) => {
                    const isSelected = inputs.area_of_work.includes(area);
                    return (
                      <button
                        key={area}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            setInputs({ ...inputs, area_of_work: inputs.area_of_work.filter(a => a !== area) });
                          } else {
                            setInputs({ ...inputs, area_of_work: [...inputs.area_of_work, area] });
                          }
                        }}
                        className={`p-2.5 rounded-lg border text-left text-sm transition-all flex items-center gap-2 ${
                          isSelected
                            ? 'bg-teal-500/20 border-teal-500 text-white'
                            : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                        }`}
                        data-testid={`area-${area.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                          isSelected ? 'bg-teal-500 border-teal-500' : 'border-zinc-600'
                        }`}>
                          {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                        </div>
                        <span className="truncate">{area}</span>
                      </button>
                    );
                  })}
                </div>
                {inputs.area_of_work.length > 0 && (
                  <p className="text-xs text-teal-400 mt-2">
                    {inputs.area_of_work.length} area{inputs.area_of_work.length > 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
              
              <div>
                <Label className="text-zinc-300 mb-2 block">Experience Level</Label>
                <div className="grid grid-cols-1 gap-2">
                  {EXPERIENCE_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setInputs({ ...inputs, experience_level: level.value })}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        inputs.experience_level === level.value
                          ? 'bg-teal-500/20 border-teal-500 text-white'
                          : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                      }`}
                      data-testid={`level-${level.value.toLowerCase()}`}
                    >
                      <div className="font-medium text-sm">{level.label}</div>
                      <div className="text-xs text-zinc-400">{level.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 mb-3">
                <Target className="h-7 w-7 text-amber-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-1">Your Goals</h2>
              <p className="text-zinc-400 text-sm">Tell us what you want to learn and where you want to go</p>
            </div>
            
            <div className="max-w-lg mx-auto space-y-5">
              <div>
                <Label htmlFor="learning_goals" className="text-zinc-300 mb-2 block">
                  <Lightbulb className="h-4 w-4 inline mr-1 text-amber-400" />
                  Learning Goals
                </Label>
                <Textarea
                  id="learning_goals"
                  placeholder="What would you like to learn or become better at?"
                  value={inputs.learning_goals}
                  onChange={(e) => setInputs({ ...inputs, learning_goals: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-white min-h-[100px]"
                  data-testid="input-learning-goals"
                />
              </div>
              
              <div>
                <Label htmlFor="career_goal" className="text-zinc-300 mb-2 block">
                  <TrendingUp className="h-4 w-4 inline mr-1 text-teal-400" />
                  Career Goal
                </Label>
                <Textarea
                  id="career_goal"
                  placeholder="What role or area would you like to progress into? Please type the full title (e.g., 'Chief Compliance Officer' not 'CCO', 'Chief Information Officer' not 'CIO')"
                  value={inputs.career_goal}
                  onChange={(e) => setInputs({ ...inputs, career_goal: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-white min-h-[100px]"
                  data-testid="input-career-goal"
                />
                <p className="text-xs text-amber-400/80 mt-1.5">
                  Tip: Avoid acronyms. Write the full role title to help NAVI better understand your career aspirations.
                </p>
              </div>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Your Competency Framework</h2>
                <p className="text-zinc-400 text-sm">
                  NAVI generated {competencies.length} competencies
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRegenerate}
                  disabled={isGenerating}
                  className="border-zinc-700"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${isGenerating ? 'animate-spin' : ''}`} />
                  Regenerate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAddDialogOpen(true)}
                  className="border-zinc-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
            
            {/* Summary */}
            <div className="bg-gradient-to-br from-teal-500/10 to-amber-500/10 rounded-xl p-4 border border-teal-500/20">
              <div className="flex items-start gap-3">
                <img src={NAVI_AVATAR} alt="NAVI" className="w-10 h-10 rounded-full" />
                <div>
                  <h3 className="font-semibold text-white mb-1">NAVI&apos;s Analysis</h3>
                  <p className="text-sm text-zinc-300">
                    Based on your role as <strong className="text-teal-400">{inputs.current_role}</strong> in{' '}
                    <strong className="text-teal-400">{Array.isArray(inputs.area_of_work) ? inputs.area_of_work.join(', ') : inputs.area_of_work}</strong>, I&apos;ve identified key competencies 
                    to progress towards <strong className="text-amber-400">{inputs.career_goal}</strong>.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Competencies List */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {competencies.map((competency, index) => (
                <div
                  key={competency.competency_id}
                  className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700"
                  data-testid={`competency-card-${index}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-white">{competency.name}</h3>
                        <Badge className={LEVEL_COLORS[competency.target_level] || 'bg-zinc-500/20'}>
                          {competency.target_level}
                        </Badge>
                      </div>
                      <Badge variant="outline" className={CATEGORY_COLORS[competency.category] || 'border-zinc-600'}>
                        {competency.category}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(competency)}
                        className="text-zinc-400 hover:text-white h-8 w-8 p-0"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(competency.competency_id)}
                        className="text-zinc-400 hover:text-red-400 h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <p className="text-sm text-zinc-400">{competency.definition}</p>
                  <div className="mt-2 pt-2 border-t border-zinc-700/50 text-xs text-zinc-500">
                    <strong className="text-zinc-400">Outcome:</strong> {competency.measurable_outcome}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Save */}
            <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
              <div className="flex items-center gap-3 mb-3">
                <Label htmlFor="framework_name" className="text-zinc-300 whitespace-nowrap text-sm">Name:</Label>
                <Input
                  id="framework_name"
                  value={frameworkName}
                  onChange={(e) => setFrameworkName(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white h-9"
                  data-testid="input-framework-name"
                />
              </div>
              
              <Button
                onClick={handleSave}
                disabled={isSaving || competencies.length === 0}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white"
                data-testid="btn-save-framework"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Competency Framework
              </Button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  // Render create wizard view
  const renderCreateView = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={NAVI_AVATAR} alt="NAVI" className="w-10 h-10 rounded-full" />
          <div>
            <h2 className="text-xl font-bold text-white">Competency Framework Generator</h2>
            <p className="text-sm text-zinc-400">Powered by NAVI</p>
          </div>
        </div>
        {savedFrameworks.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setViewMode('saved'); resetWizard(); }}
            className="border-zinc-700"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Saved
          </Button>
        )}
      </div>
      
      {/* Progress Bar */}
      {step < 4 && (
        <div>
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="text-zinc-400">Step {step} of {totalSteps}</span>
            <span className="text-zinc-400">{Math.round((step / totalSteps) * 100)}%</span>
          </div>
          <Progress value={(step / totalSteps) * 100} className="h-1.5 bg-zinc-800" />
          
          {/* Step indicators */}
          <div className="flex justify-between mt-3">
            {['Current Role', 'Area & Level', 'Goals', 'Framework'].map((label, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                  i + 1 < step ? 'bg-teal-500 text-white' :
                  i + 1 === step ? 'bg-teal-500/20 text-teal-400 border-2 border-teal-500' :
                  'bg-zinc-800 text-zinc-500'
                }`}>
                  {i + 1 < step ? <CheckCircle className="h-4 w-4" /> : i + 1}
                </div>
                <span className={`text-xs mt-1 ${i + 1 <= step ? 'text-zinc-300' : 'text-zinc-600'}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-6">
        {renderStepContent()}
      </div>
      
      {/* Navigation */}
      {step < 4 && (
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => step === 1 && savedFrameworks.length > 0 ? setViewMode('saved') : setStep(Math.max(1, step - 1))}
            className="border-zinc-700"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          
          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-teal-500 hover:bg-teal-600"
              data-testid="btn-next"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleGenerate}
              disabled={!canProceed() || isGenerating}
              className="bg-gradient-to-r from-teal-500 to-amber-500 hover:from-teal-600 hover:to-amber-600"
              data-testid="btn-generate"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  NAVI is analysing...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Competencies
                </>
              )}
            </Button>
          )}
        </div>
      )}
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Competency</DialogTitle>
          </DialogHeader>
          
          {editingCompetency && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-300">Name</Label>
                <Input
                  value={editingCompetency.name}
                  onChange={(e) => setEditingCompetency({ ...editingCompetency, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Category</Label>
                <Select
                  value={editingCompetency.category}
                  onValueChange={(value) => setEditingCompetency({ ...editingCompetency, category: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {COMPETENCY_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat} className="text-white">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-300">Target Level</Label>
                <Select
                  value={editingCompetency.target_level}
                  onValueChange={(value) => setEditingCompetency({ ...editingCompetency, target_level: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {TARGET_LEVELS.map((level) => (
                      <SelectItem key={level} value={level} className="text-white">
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-300">Definition</Label>
                <Textarea
                  value={editingCompetency.definition}
                  onChange={(e) => setEditingCompetency({ ...editingCompetency, definition: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Measurable Outcome</Label>
                <Textarea
                  value={editingCompetency.measurable_outcome}
                  onChange={(e) => setEditingCompetency({ ...editingCompetency, measurable_outcome: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="bg-teal-500 hover:bg-teal-600">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Competency</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Name</Label>
              <Input
                value={newCompetency.name}
                onChange={(e) => setNewCompetency({ ...newCompetency, name: e.target.value })}
                placeholder="Competency name"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">Category</Label>
              <Select
                value={newCompetency.category}
                onValueChange={(value) => setNewCompetency({ ...newCompetency, category: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {COMPETENCY_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-white">
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-zinc-300">Target Level</Label>
              <Select
                value={newCompetency.target_level}
                onValueChange={(value) => setNewCompetency({ ...newCompetency, target_level: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {TARGET_LEVELS.map((level) => (
                    <SelectItem key={level} value={level} className="text-white">
                      {level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-zinc-300">Definition</Label>
              <Textarea
                value={newCompetency.definition}
                onChange={(e) => setNewCompetency({ ...newCompetency, definition: e.target.value })}
                placeholder="What this competency means"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">Measurable Outcome</Label>
              <Textarea
                value={newCompetency.measurable_outcome}
                onChange={(e) => setNewCompetency({ ...newCompetency, measurable_outcome: e.target.value })}
                placeholder="What the user must demonstrate"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button 
              onClick={handleAdd} 
              disabled={!newCompetency.name || !newCompetency.category}
              className="bg-teal-500 hover:bg-teal-600"
            >
              Add Competency
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
  
  return (
    <div className="bg-navigate-surface rounded-xl border border-zinc-800 p-6">
      {viewMode === 'saved' ? renderSavedView() : renderCreateView()}
    </div>
  );
}
